/**
 * Company Edit Page
 * Page for editing existing companies
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import { CompanyForm } from './components';
import { PageLayout, PageBreadcrumb } from '../../components';

// Services
import { companyService } from '../../services/companyService';

// Types
import { Company, CompanyFormData, CompanyContact, ContactType, ModuleType, CompanyType, CompanyStatus, DocumentType, ESocialSendMode, UpdateCompanyRequest } from '../../types/company';

const CompanyEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [company, setCompany] = useState<Company | null>(null);
  const [existingContacts, setExistingContacts] = useState<CompanyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load company data
  const loadCompany = async () => {
    if (!id) {
      setError('ID da empresa não fornecido');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const companyData = await companyService.getCompanyById(id);
      setCompany(companyData);
      // Load existing contacts for comparison during save
      try {
        const contacts = await companyService.getContacts(id);
        setExistingContacts(contacts);
      } catch { /* No contacts yet */ }
    } catch (err: any) {
      setError(err.message ?? 'Erro ao carregar dados da empresa');
      enqueueSnackbar('Erro ao carregar dados da empresa', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (data: CompanyFormData) => {
    if (!id) return;

    setSaving(true);
    
    try {
      // CompanyForm already converts modules to ModuleType[]
      const modules = data.modules as unknown as ModuleType[];
      
      const updateRequest: UpdateCompanyRequest = {
        legalName: data.legalName,
        tradeName: data.tradeName ?? undefined,
        cnpj: data.cnpj,
        state: data.state,
        zipCode: data.zipCode,
        city: data.city ?? undefined,
        neighborhood: data.neighborhood ?? undefined,
        address: data.address ?? undefined,
        addressNumber: data.addressNumber ?? undefined,
        companyType: data.companyType ? (data.companyType as CompanyType) : undefined,
        status: data.status as CompanyStatus,
        modules,
        // Extended fields
        stateRegistration: data.stateRegistration || undefined,
        municipalRegistration: data.municipalRegistration || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        whatsapp: data.whatsapp || undefined,
        ibgeCode: data.ibgeCode || undefined,
        documentType: data.documentType ? (data.documentType as DocumentType) : undefined,
        documentNumber: data.documentNumber || undefined,
        ecacExpirationDate: data.ecacExpirationDate || undefined,
        esocialSendMode: data.esocialSendMode ? (data.esocialSendMode as ESocialSendMode) : undefined,
        conditionStartDate: data.conditionStartDate || undefined,
        parentCompanyId: data.parentCompanyId ? Number(data.parentCompanyId) : undefined,
      };

      await companyService.updateCompany(id, updateRequest);

      // Handle contacts: compare form contacts vs existing
      // `Array.isArray` e não `|| []`: o formulário guarda os contatos como objeto indexado por
      // tipo enquanto a pessoa digita, e um objeto passa pelo `||` inteiro — foi assim que um
      // salvamento bem-sucedido terminava em erro vermelho.
      const contatosDoFormulario = (data as any).contacts as unknown;
      const formContacts: Array<{ contactType: string; name: string; email?: string; phone?: string }> =
        Array.isArray(contatosDoFormulario) ? contatosDoFormulario : [];
      const formContactTypes = new Set(formContacts.map(c => c.contactType));
      const existingContactTypes = new Map(existingContacts.map(c => [c.contactType, c]));

      // Create or update contacts from form
      for (const contact of formContacts) {
        const existing = existingContactTypes.get(contact.contactType as ContactType);
        try {
          if (existing) {
            await companyService.updateContact(id, String(existing.id), {
              contactType: contact.contactType as ContactType,
              name: contact.name,
              email: contact.email,
              phone: contact.phone,
            });
          } else {
            await companyService.createContact(id, {
              contactType: contact.contactType as ContactType,
              name: contact.name,
              email: contact.email,
              phone: contact.phone,
              isActive: true,
            });
          }
        } catch (err) {
          console.warn('Contact save failed:', err);
        }
      }

      // Delete contacts that were emptied (existed before but not in form now)
      for (const existing of existingContacts) {
        if (!formContactTypes.has(existing.contactType)) {
          try {
            await companyService.deleteContact(id, String(existing.id));
          } catch (err) {
            console.warn('Contact delete failed:', err);
          }
        }
      }
      enqueueSnackbar('Empresa atualizada com sucesso!', { variant: 'success' });
      navigate('/companies');
    } catch (error: any) {
      console.error('Error updating company:', error);
      enqueueSnackbar(error.message ?? 'Erro ao atualizar empresa', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/companies');
  };

  // Load data on mount
  useEffect(() => {
    loadCompany();
  }, [id]);

  // Loading state
  if (loading) {
    return (
      <PageLayout>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      </PageLayout>
    );
  }

  // Error state
  if (error || !company) {
    return (
      <PageLayout>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Dados da empresa não encontrados'}
          </Alert>
          <Link component="button" onClick={() => navigate('/companies')}>
            Voltar para lista de empresas
          </Link>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <PageBreadcrumb
            items={[
              { label: 'Início', path: '/' },
              { label: 'Empresas', path: '/companies' },
              { label: 'Editar Empresa' },
            ]}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h4" component="h1">
              Editar Empresa
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            {company.tradeName || company.legalName} - {company.cnpj}
          </Typography>
        </Box>

        {/* Form Card */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <CompanyForm
              company={company}
              mode="edit"
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={saving}
            />
          </CardContent>
        </Card>
      </Container>
      </PageLayout>
  );
};

export default CompanyEditPage;